import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import Tooltip from '../ui/Tooltip';
import DropdownMenu from '../ui/DropdownMenu';

import {
  PlusIcon,
  UploadIcon,
  RecordIcon,
  LibraryIcon
} from '../../utils/icons';

const ActionButton = ({ label, onClick, showTooltip, tooltipText, onMouseEnter, onMouseLeave, buttonRef }) => (
  <div
    ref={buttonRef}
    className="flex justify-center items-center flex-1 h-full relative gap-2 cursor-pointer hover:opacity-80 transition-opacity"
    style={{ backgroundColor: colors.bg.fog.thin }}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <div className="w-4 h-4 flex items-center justify-center">
      <PlusIcon style={{ width: '16px', height: '16px' }} />
    </div>
    <p style={{ ...typography.small.title, color: colors.text.primary }}>
      {label}
    </p>
  </div>
);

const CustomActions = ({ onAudioClick, onPersonaClick, showAudioButton = true, showPersonaButton = true }) => {
  const [isPersonaHovered, setIsPersonaHovered] = useState(false);
  const [isAudioHovered, setIsAudioHovered] = useState(false);
  const [showAudioDropdown, setShowAudioDropdown] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [audioTooltipPosition, setAudioTooltipPosition] = useState({ x: 0, y: 0 });
  const personaButtonRef = useRef(null);
  const audioButtonRef = useRef(null);
  const personaTimeoutRef = useRef(null);
  const audioTimeoutRef = useRef(null);

  // Audio dropdown options
  const audioOptions = [
    {
      value: 'upload',
      label: 'Upload (audio or video)',
      icon: <UploadIcon style={{ width: '16px', height: '16px' }} />
    },
    {
      value: 'record',
      label: 'Record',
      icon: <RecordIcon style={{ width: '16px', height: '16px' }} />
    },
    {
      value: 'library',
      label: 'Library',
      icon: <LibraryIcon style={{ width: '16px', height: '16px' }} />
    }
  ];

  useEffect(() => {
    if (isPersonaHovered && personaButtonRef.current) {
      const rect = personaButtonRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 4
      });
    }
  }, [isPersonaHovered]);

  useEffect(() => {
    if (isAudioHovered && audioButtonRef.current) {
      const rect = audioButtonRef.current.getBoundingClientRect();
      setAudioTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 4
      });
    }
  }, [isAudioHovered]);

  const handlePersonaMouseEnter = () => {
    if (personaTimeoutRef.current) {
      clearTimeout(personaTimeoutRef.current);
    }
    personaTimeoutRef.current = setTimeout(() => {
      setIsPersonaHovered(true);
    }, 300);
  };

  const handlePersonaMouseLeave = () => {
    if (personaTimeoutRef.current) {
      clearTimeout(personaTimeoutRef.current);
    }
    setIsPersonaHovered(false);
  };

  const handleAudioMouseEnter = () => {
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }
    audioTimeoutRef.current = setTimeout(() => {
      setIsAudioHovered(true);
    }, 300);
  };

  const handleAudioMouseLeave = () => {
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }
    setIsAudioHovered(false);
  };

  const handleAudioButtonClick = () => {
    setShowAudioDropdown(!showAudioDropdown);
    // Hide tooltip when dropdown opens
    setIsAudioHovered(false);
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }
  };

  const handleAudioOptionSelect = (value) => {
    setShowAudioDropdown(false);
    
    switch (value) {
      case 'upload':
        if (onAudioClick) {
          onAudioClick();
        }
        break;
      case 'record':
        // TODO: Implement record functionality
        console.log('Record clicked');
        break;
      case 'library':
        // TODO: Implement library functionality
        console.log('Library clicked');
        break;
    }
  };


  return (
    <>
      <div className="flex justify-start items-center w-full h-14 overflow-hidden gap-px rounded-2xl border border-transparent">
        {showAudioButton && (
          <ActionButton 
            label="Audio" 
            onClick={handleAudioButtonClick}
            buttonRef={audioButtonRef}
            onMouseEnter={handleAudioMouseEnter}
            onMouseLeave={handleAudioMouseLeave}
          />
        )}
        {showPersonaButton && (
          <ActionButton 
            label="Persona" 
            onClick={onPersonaClick}
            buttonRef={personaButtonRef}
            onMouseEnter={handlePersonaMouseEnter}
            onMouseLeave={handlePersonaMouseLeave}
          />
        )}
      </div>
      
      {isPersonaHovered && tooltipPosition.x > 0 && ReactDOM.createPortal(
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%)'
          }}
        >
          <Tooltip
            text="Imitate a vocal or instrument"
            isVisible={true}
            position="top"
            offset={4}
          />
        </div>,
        document.body
      )}
      
      {isAudioHovered && audioTooltipPosition.x > 0 && !showAudioDropdown && ReactDOM.createPortal(
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: audioTooltipPosition.x,
            top: audioTooltipPosition.y,
            transform: 'translateX(-50%)'
          }}
        >
          <Tooltip
            text="Upload, record, or choose from library"
            isVisible={true}
            position="top"
            offset={4}
          />
        </div>,
        document.body
      )}
      
      {/* Audio Dropdown */}
      {showAudioDropdown && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] pointer-events-none">
          <div 
            className="absolute pointer-events-auto"
            style={{
              left: audioButtonRef.current?.getBoundingClientRect().left || 0,
              top: (audioButtonRef.current?.getBoundingClientRect().bottom || 0) + 8
            }}
          >
            <DropdownMenu
              isOpen={showAudioDropdown}
              onClose={() => setShowAudioDropdown(false)}
              options={audioOptions}
              onSelect={handleAudioOptionSelect}
              position="bottom-left"
              width={210}
              showCheck={false}
              anchorRef={audioButtonRef}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CustomActions;